/**
 * Phase 408 wave 3 task 3.2 (ADR-089) — React Query hooks for the console chat panel.
 *
 * <b>POLL, don't push</b> — the delivery-mechanism decision card #1490 delegated. There is
 * no SSE / WebSocket / SignalR anywhere in `platform/` (no `text/event-stream`, no
 * `MapHub`, no `AddSignalR`), and every live-ish surface in this console already polls at
 * the D-FETCH-01 cadence over ETag'd GETs. Inventing push infrastructure for one panel
 * would be net-new transport for a single screen; the chat panel rides the rails that
 * already exist:
 *
 *   · cadence — `POLL_OPTIONS` imported from queries.ts (4s, paused on a hidden tab). The
 *     constant is imported, never copied: "the console's live cadence" must be ONE number.
 *   · cost — `fetchWithAuth` sends `If-None-Match` and the messages route builds its weak
 *     ETag from the conversation's high-water `seq` BEFORE it reads any page
 *     (ConsoleChatEndpoints.ReadConversationMessagesAsync), so an idle 4s poll costs a 304
 *     with no body and no page query.
 *   · latency — a uat answer takes ~12s, so up to one poll interval of extra wait is noise
 *     against the agent turn itself. What the UX owes the operator is not speed but
 *     HONESTY: ChatScreen renders an explicit working line (see `isThreadWorking`)
 *     rather than leaving a sent question looking dropped.
 *
 * #2370 — the thread poll watches ONE conversation: the one on screen. On the 28 Sep uk-uat
 * walk three questions were in flight on three new conversations, the agent runs took 1.5 to
 * 3 minutes, and each reply landed while a DIFFERENT conversation was open. The engine log
 * shows the server answering every read after each reply with a 200; nothing on screen said
 * which conversations were still being worked on, and the list (read once, on open) never
 * moved. So the list now carries each conversation's own working state (`working`) and
 * rides the same cadence while any conversation in it is working
 * (`conversationsRefetchInterval`), going quiet again once every turn has ENDED — answered
 * or not. The engine decides `working` (the turn's job and agent-run events, capped at
 * ConsoleConversationStore.WorkingAgeCapSeconds), so a turn that fails or ends without a
 * reply cannot keep the label, or the poll, alive.
 */
import { useQuery } from "@tanstack/react-query";
import { fetchWithAuth } from "./fetcher.js";
import { POLL_OPTIONS } from "./queries.js";
import {
  DIRECTION_INBOUND,
  chatConversationsPath,
  chatMessagesPath,
} from "./chatRoutes.js";
import type {
  ConsoleChatMessageDto,
  ConsoleConversationDto,
  ConsoleConversationMessagesPage,
  ConsoleConversationsPage,
} from "./types.js";

/**
 * Hard bound on the cursor walk below. 25 pages × the 200-turn page limit = 5 000 turns,
 * far past any real console conversation; the bound exists so a server that kept handing
 * back a cursor could never spin the panel's fetch loop forever.
 */
export const MAX_PAGE_WALK = 25;

/** One conversation, whole, as the panel renders it. */
export interface ChatTranscript {
  messages: ConsoleChatMessageDto[];
  /** The conversation's high-water seq, straight off the wire. */
  maxSeq: number;
  /** #2370 — the server's `working` for this conversation (the platform is still on its last turn). */
  working: boolean;
  /** True only if the walk hit MAX_PAGE_WALK with a cursor still outstanding. */
  truncated: boolean;
}

/**
 * Read one conversation WHOLE, following `next_cursor` to the end.
 *
 * <b>Why re-read everything on each poll instead of appending only what is new.</b> A
 * gated card's `confirm_state` is LIVE state, not a property of the turn it arrived on: a
 * `write_confirm` that read `pending` when it landed reads `executed` the moment the write
 * runs — from this panel, from Teams, or from a colleague's session. A panel that appended
 * new turns and never revisited old ones would keep offering Confirm on a spent token, and
 * the operator's tap could only be refused. Re-reading is what makes "disable once the read
 * API reports the token spent" true rather than aspirational, and the ETag makes the idle
 * case free anyway (one conditional request, 304, no body).
 *
 * `next_cursor` is non-null EXACTLY when more turns exist (the server probes one row past
 * the limit), so the walk terminates on the real end of the thread — never on the guess
 * "a full page probably means more", and never by silently dropping the tail the way the
 * default-100 events route does (the footgun ADR-089 was written to avoid).
 */
export async function fetchTranscript(
  tenantId: string,
  conversationId: string,
): Promise<ChatTranscript> {
  const messages: ConsoleChatMessageDto[] = [];
  let after = 0;
  let maxSeq = 0;
  let working = false;

  for (let page = 0; page < MAX_PAGE_WALK; page++) {
    const res = await fetchWithAuth<ConsoleConversationMessagesPage>(
      chatMessagesPath(tenantId, conversationId, after),
    );
    messages.push(...res.messages);
    maxSeq = res.max_seq;
    working = res.working;
    if (res.next_cursor === null) return { messages, maxSeq, working, truncated: false };
    after = res.next_cursor;
  }

  return { messages, maxSeq, working, truncated: true };
}

/**
 * The 4s poll on one conversation (D-FETCH-01 cadence via POLL_OPTIONS).
 *
 * Disabled until BOTH a tenant and a conversation are chosen — a chat read is
 * tenant-scoped by the operator guard and owner-scoped by the principal, and firing it
 * with an empty tenant would just earn an `invalid_tenant` 400 on a loop.
 */
export function useChatMessages(tenantId: string, conversationId: string) {
  return useQuery({
    queryKey: ["chat-messages", tenantId, conversationId] as const,
    queryFn: () => fetchTranscript(tenantId, conversationId),
    ...POLL_OPTIONS,
    enabled: !!tenantId && !!conversationId,
  });
}

/**
 * The calling principal's conversations in one tenant (ADR-089 D2 — a colleague's threads
 * in the same tenant are not visible here, by construction on the server).
 *
 * NOT on the 4s cadence by default. The server's own note on this route
 * (ConsoleChatEndpoints.ListConversationsAsync) is that its ETag saves the body but not
 * the query, because the list is read when the panel OPENS while the poll rides the
 * messages route. So this query refreshes on mount and on invalidation — the send and
 * decision mutations both invalidate it, which is what moves a new conversation into the
 * list the instant it exists — and polls only while a conversation in it is working
 * (#2370, `conversationsRefetchInterval`): a bounded window, one agent turn long.
 */
export function useChatConversations(tenantId: string) {
  return useQuery({
    queryKey: ["chat-conversations", tenantId] as const,
    queryFn: () =>
      fetchWithAuth<ConsoleConversationsPage>(chatConversationsPath(tenantId)),
    // #2370: on the live cadence ONLY while a conversation in the list is working, so a reply
    // that lands on a conversation you are not looking at still shows up in the list.
    refetchInterval: (query) => conversationsRefetchInterval(query.state.data),
    refetchIntervalInBackground: POLL_OPTIONS.refetchIntervalInBackground,
    staleTime: 30_000,
    enabled: !!tenantId,
  });
}

/**
 * #2370 — true while the platform is still working on this conversation's last turn: the
 * engine's `working`, which ends when the turn ends (a reply, the job going terminal, the
 * agent run ending with nothing to say) and never outlives the engine's age cap. NOT "the
 * operator spoke last" on its own: a turn that ended without a reply still ends with the
 * operator's message, and reading that as working is a label and a poll that never stop.
 */
export function isConversationWorking(conversation: ConsoleConversationDto): boolean {
  return conversation.working;
}

/**
 * #2370 — the conversation list's poll: the console's live cadence while any listed
 * conversation is working, off otherwise. Pure, so the "when does the list poll" rule is
 * tested rather than buried in the hook.
 */
export function conversationsRefetchInterval(
  page: ConsoleConversationsPage | undefined,
): number | false {
  return page?.conversations.some(isConversationWorking) ? POLL_OPTIONS.refetchInterval : false;
}

/**
 * True when the last turn on the thread is the operator's own — i.e. the platform owes an
 * answer. Pure, and exported so the awaiting-reply affordance is one rule rather than a
 * conditional buried in JSX.
 *
 * A clarification card is the platform's turn, so an operator being ASKED something is
 * correctly not "awaiting a reply" — the ball is theirs.
 */
export function isAwaitingReply(messages: readonly ConsoleChatMessageDto[]): boolean {
  const last = messages[messages.length - 1];
  return !!last && last.direction === DIRECTION_INBOUND;
}

/** What the thread's working indicator is decided from (see `isThreadWorking`). */
export interface ThreadWorkingInputs {
  /** The conversation on screen ("" for a conversation not started yet). */
  conversationId: string;
  /** The turns the thread query currently holds for it. */
  messages: readonly ConsoleChatMessageDto[];
  /** The server's `working` from that same read (false when nothing has been read). */
  turnOpen: boolean;
  /** `dataUpdatedAt` of that thread query: when it was last read (0 = never). */
  threadUpdatedAt: number;
  /** A send in flight, and the conversation it was sent from ("" when it starts one). */
  pendingSend?: { conversationId: string };
  /** The last send the server accepted: the conversation it landed on, and when it was accepted. */
  acceptedSend?: { conversationId: string; acceptedAt: number };
}

/**
 * #2370 — should the open thread show that the platform is working on a reply?
 *
 * Driven by the conversation's own state, from the moment Send is pressed until the platform's
 * next turn lands:
 *
 *   1. a send is in flight from THIS conversation → working (the instant Send is pressed);
 *   2. the server accepted a send onto this conversation and the thread has not been read since
 *      → working. This bridges the gap between the 202 and the first read of the thread: the
 *      send's own turn is committed before the 202, but until the panel has read it, the thread
 *      it holds is either nothing (a conversation the send just started) or the turns BEFORE the
 *      send, which end with the previous reply and would read as idle;
 *   3. otherwise the thread itself decides: the operator spoke last (`isAwaitingReply`) AND
 *      the engine says the turn is still open (`turnOpen`). The reply lands → not working;
 *      the turn ends with NO reply (the job failed or was cancelled, the run ended with
 *      nothing to say, the engine's age cap passed) → not working either.
 *
 * Every arm is bounded: 1 by the send request, 2 by the next read of the thread, 3 by the
 * engine's own end-of-turn signal and cap.
 */
export function isThreadWorking(inputs: ThreadWorkingInputs): boolean {
  const { conversationId, messages, turnOpen, threadUpdatedAt, pendingSend, acceptedSend } = inputs;
  if (pendingSend && pendingSend.conversationId === conversationId) return true;
  if (
    acceptedSend &&
    acceptedSend.conversationId === conversationId &&
    threadUpdatedAt < acceptedSend.acceptedAt
  ) {
    return true;
  }
  return turnOpen && isAwaitingReply(messages);
}
